package pt.at.oss.kotlin.core.enumeration

import pt.at.oss.kotlin.core.enumeration.PeculiarCountry.IssuerMode.ANY
import pt.at.oss.kotlin.core.enumeration.PeculiarCountry.IssuerMode.PARENT
import pt.at.oss.kotlin.core.enumeration.PeculiarCountry.IssuerMode.REGION

/**
 * Country codes that either:
 * - are not in AT's catalog;
 * - have an assigned parent country.
 * Created by Pedro Alfaiate on 10/02/2021.
 */
enum class PeculiarCountry(
    val isUnion: Boolean,
    val isNonUnion: Boolean,
    override val codeA2: String,
    val parentA2: String,
    val labelPT: String,
    val labelEN: String,
    val issuerMode: IssuerMode // RFC-960 - When used as region/territory, it must be equal to the country of business
) : WithCodeA2 {
    GREENLAND(false, true, "GL", "DK", "Greenland", "Greenland", REGION), // -> issuedBy=GL
    FAROE_ISLANDS(false, true, "FO", "DK", "Faroe Islands", "Faroe Islands", REGION), // -> issuedBy=FO

    ALAND_ISLANDS(false, true, "AX", "FI", "Åland Islands", "Åland Islands", REGION), // -> issuedBy=AX

    SAINT_MARTIN(false, true, "MF", "FR", "Saint Martin", "Saint Martin", PARENT), // -> issuedBy=FR
    MONACO(true, false, "MC", "FR", "Monaco", "Monaco", PARENT), // -> issuedBy=FR...
    FRENCH_GUIANA(false, true, "GF", "FR", "French Guiana", "French Guiana", PARENT),
    FRENCH_POLYNESIA(false, true, "PF", "FR", "French Polynesia", "French Polynesia", PARENT),
    FRENCH_SOUTHERN_ANTARCTIC(false, true, "TF", "FR", "French Southern and Antarctic Lands", "French Southern and Antarctic Lands", PARENT),
    GUADELOUPE(false, true, "GP", "FR", "Guadeloupe", "Guadeloupe", PARENT),
    MARTINIQUE(false, true, "MQ", "FR", "Martinique", "Martinique", PARENT),
    MAYOTTE(false, true, "YT", "FR", "Mayotte", "Mayotte", PARENT),
    NEW_CALEDONIA(false, true, "NC", "FR", "New Caledonia", "New Caledonia", PARENT),
    REUNION(false, true, "RE", "FR", "Réunion", "Réunion", PARENT),
    SAINT_PIERRE_MIQUELON(false, true, "PM", "FR", "Saint Pierre and Miquelon", "Saint Pierre and Miquelon", PARENT),
    WALLIS_FUTUNA(false, true, "WF", "FR", "Wallis and Futuna", "Wallis and Futuna", PARENT),
    SAINT_BARTHELEMY(false, true, "BL", "FR", "São Bartolomeu", "Saint Barthélemy", PARENT),

    HELIOGOLAND(false, true, "XA", "DE", "Heliogoland", "Heliogoland", PARENT), // -> issuedBy=DE
    BUESINGEN(false, true, "XB", "DE", "Buesingen", "Buesingen", PARENT), // -> issuedBy=DE

    // [OSS-1552][OSS-1609] O codeA2 foi alterado de GR para EL, porque no OSS usamos EL como código da Grécia apesar
    // do código ISO-3166 ser GR
    MONTE_ATHOS(false, true, "XM", "EL", "Monte Athos", "Mount Athos", PARENT), // -> issuedBy=EL

    CAMPIONE_ITALIA(false, true, "XD", "IT", "Campione d'Italia", "Campione d'Italia", PARENT), // -> RFC-1112
    LAGO_LUGANO(false, true, "XE", "IT", "Lago Lugano", "Lake Lugano", PARENT), // -> RFC-1112
    LIVIGNO(false, true, "XF", "IT", "Livigno", "Livigno", PARENT), // -> RFC-1112

    ARUBA(false, true, "AW", "NL", "Aruba", "Aruba", REGION), // -> issuedBy=AW
    CURACAO(false, true, "CW", "NL", "Curaçao", "Curaçao", REGION), // -> issuedBy=CW
    SINT_MAARTEN(false, true, "SX", "NL", "Sint Maarten (Dutch part)", "Sint Maarten (Dutch part)", REGION), // -> issuedBy=SX
    BONAIRE_EUSTATIUS_SABA(false, true, "BQ", "NL", "Bonaire, Sint Eustatius and Saba", "Bonaire, Sint Eustatius and Saba", REGION), // -> issuedBy=BQ

    ILHAS_CANARIAS(false, true, "IC", "ES", "Ilhas Canárias", "Canary Islands", ANY),
    CEUTA(false, true, "XC", "ES", "Ceuta", "Ceuta", ANY),
    MELILLA(false, true, "XL", "ES", "Melilla", "Melilla", ANY),

    UK_WITHOUT_NORTH_IRELAND(false, true, "XU", "GB", "Reino Unido (sem Irlanda do Norte)", "United Kingdom (without Northern Ireland)", ANY),
    NORTHERN_IRELAND(true, true, "XI", "GB", "Irlanda do Norte", "Northern Ireland", ANY),
    ALDERNEY(false, true, "XG", "GB", "Alderney", "Alderney", ANY),
    SARK(false, true, "XH", "GB", "Sark", "Sark", ANY),
    ANGUILLA(false, true, "AI", "GB", "Anguilla", "Anguilla", ANY),
    BERMUDA(false, true, "BM", "GB", "Bermuda", "Bermuda", ANY),
    BRITISH_INDIAN_OCEAN(false, true, "IO", "GB", "British Indian Ocean Territory", "British Indian Ocean Territory", ANY),
    BRITISH_VIRGIN_ISLANDS(false, true, "VG", "GB", "British Virgin Islands", "British Virgin Islands", ANY),
    CAYMAN_ISLANDS(false, true, "KY", "GB", "Cayman Islands", "Cayman Islands", ANY),
    FALKLAND_ISLANDS(false, true, "FK", "GB", "Falkland Islands", "Falkland Islands", ANY),
    GIBRALTAR(false, true, "GI", "GB", "Gibraltar", "Gibraltar", ANY),
    BAILIWICK_OF_GUERNSEY(false, true, "GG", "GB", "Bailiwick of Guernsey", "Bailiwick of Guernsey", ANY),
    BAILIWICK_OF_JERSEY(false, true, "JE", "GB", "Bailiwick of Jersey", "Bailiwick of Jersey", ANY),
    MONTSERRAT(false, true, "MS", "GB", "Montserrat", "Montserrat", ANY),
    PITCAIRN_ISLANDS(false, true, "PN", "GB", "Pitcairn Islands", "Pitcairn Islands", ANY),
    S_HELENA_ASC_TRISTAN_CUNHA(false, true, "SH", "GB", "Saint Helena, Ascension and Tristan da Cunha", "Saint Helena, Ascension and Tristan da Cunha", ANY),
    S_GEORGIA_S_SANDWICH_ISLANDS(false, true, "GS", "GB", "South Georgia and the South Sandwich Islands", "South Georgia and the South Sandwich Islands", ANY),
    TURKS_CAICOS_ISLANDS(false, true, "TC", "GB", "Turks and Caicos Islands", "Turks and Caicos Islands", ANY),
    ISLE_MAN(false, true, "IM", "GB", "Isle of Man", "Isle of Man", ANY),

    ;
    enum class IssuerMode {
        PARENT, REGION, ANY
    }

    companion object {
        fun findByA2(codeA2: String?): PeculiarCountry? =
            values().find { it.codeA2 == codeA2 }

        val ARTIFICIAL_CODE_ADDRESS = listOf(
            HELIOGOLAND.codeA2, // XA
            BUESINGEN.codeA2, // XB
            CEUTA.codeA2, // XC
            CAMPIONE_ITALIA.codeA2, // XD
            LAGO_LUGANO.codeA2, // XE
            LIVIGNO.codeA2, // XF
            ALDERNEY.codeA2, // XG
            SARK.codeA2, // XH
            NORTHERN_IRELAND.codeA2, // XI
            MELILLA.codeA2, // XL
            MONTE_ATHOS.codeA2, // XM
            UK_WITHOUT_NORTH_IRELAND.codeA2 // XU
        )
    }
}
